/**
 * `P0-UIK-010`: gender and number agreement, section 06, 6.5.5.
 *
 * **The mockups handle agreement deliberately and the product must keep doing
 * so.** French agrees participles with the thing they describe , *attestation
 * enregistrée*, *bulletins enregistrés* , and the lazy escape is `(e)` and
 * `(s)`. Canon forbids both, and it is not a matter of taste: `enregistré(e)`
 * is the sentence of a form nobody proofread, on a screen a bank is deciding
 * whether to trust with six million francs.
 *
 * Two mechanisms, no third (6.5.5): a LABEL agrees through its `accord` tag,
 * and a PERSON agrees through the role label , `identity.roles.label` and
 * `label_feminine` , never through a gender column. The one exception is the
 * client, who has `clients.gender`, because messages address them directly.
 */

/** Feminine or masculine, singular or plural. Carried by every labelled object. */
export type Accord = "fs" | "ms" | "fp" | "mp";

export const ACCORDS: readonly Accord[] = ["fs", "ms", "fp", "mp"];

/**
 * 6.5.5's own function, verbatim in behaviour: the feminine adds `e`, the
 * plural adds `s`, and the feminine plural adds both.
 *
 *   accorde("enregistré", "fs") -> "enregistrée"   Attestation enregistrée
 *   accorde("enregistré", "mp") -> "enregistrés"   Bulletins enregistrés
 *
 * The default is `fs` because canon's signature defaults to it, and because
 * the commonest labelled object in this product is a `pièce`, which is
 * feminine singular.
 */
export const accorde = (mot: string, accord: Accord = "fs"): string =>
  mot + (accord.startsWith("f") ? "e" : "") + (accord.endsWith("p") ? "s" : "");

/**
 * The French plural rule, which is NOT the English one: the plural starts at
 * **2**, so `0 dossier` and `1 dossier` are singular and `2 dossiers` is not.
 * An English-trained pluraliser writes `0 dossiers`, which is wrong in French
 * and is the sort of wrongness a Malian banker notices immediately.
 */
export function pl(n: number, singulier: string, pluriel?: string): string {
  const mot = Math.abs(n) >= 2 ? (pluriel ?? `${singulier}s`) : singulier;
  return `${String(n)} ${mot}`;
}
